import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { Button, PageHeader, PageLayout, Skeleton } from '@keboola/design';
import { ArrowLeft } from '@keboola/design/icons';

import { QueryError } from '../../../../components/QueryError';
import { useAdminApp, useAdminUpdateApp } from '../../../../features/admin/queries';
import { EditAppForm } from '../../../../features/apps/editForm/EditAppForm';

export const Route = createFileRoute('/admin/components/$appId/edit')({
  component: AdminComponentEditPage,
  head: () => ({ meta: [{ title: 'Edit component | Keboola Developer Portal admin' }] }),
});

/**
 * The vendor edit form with `audience="admin"`, which adds and sends the admin-only fields.
 *
 * - Saves via `PATCH /admin/apps/{id}`, hence `onSave` as a prop.
 */
function AdminComponentEditPage() {
  const { appId } = Route.useParams();
  const navigate = useNavigate();
  const { data: app, isLoading, isError, error, refetch } = useAdminApp(appId);
  const updateApp = useAdminUpdateApp();

  return (
    // `narrow`, as on the vendor edit route: at `full` width one column of controls spans the window.
    <PageLayout maxWidth="narrow">
      <PageLayout.Header>
        <PageHeader.Detail
          title={app?.name ?? appId}
          description="Every field, including the ones a vendor cannot set."
          breadcrumbs={
            <Button asChild variant="inline">
              <Link to="/admin/components/$appId" params={{ appId }}>
                <ArrowLeft />
                Back to the component
              </Link>
            </Button>
          }
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-96" />}

      {/* `titleAs="h2"`: the default `h3` would skip a level under the page's `h1`. */}
      <QueryError
        isError={isError}
        hasData={app !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load this component"
        refreshTitle="Could not refresh this component"
        staleNote="Showing the version from the last successful load."
        titleAs="h2"
      />

      {app !== undefined && (
        <EditAppForm
          // Keyed: the route stays mounted across `$appId`, and an edited form ignores new
          // `defaultValues` — it would `PATCH` one component's values onto another.
          key={app.id}
          app={app}
          audience="admin"
          onSave={(input) => updateApp.mutateAsync({ appId, input })}
          onSaved={() => void navigate({ to: '/admin/components/$appId', params: { appId } })}
        />
      )}
    </PageLayout>
  );
}
